import React, { useState, useEffect } from 'react';
import { useLanguageStore, useCartStore } from '../store';
import { ChevronDown, ChevronUp, ShoppingCart, Plus, Minus, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { translations } from '../translations';
import toast from 'react-hot-toast';

const FALLBACK_IMAGE = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22400%22 viewBox=%220 0 400 400%22%3E%3Crect fill=%22%23e2e8f0%22 width=%22400%22 height=%22400%22/%3E%3Ctext fill=%22%2394a3b8%22 font-family=%22sans-serif%22 font-size=%2220%22 x=%2250%25%22 y=%2250%25%22 dominant-baseline=%22middle%22 text-anchor=%22middle%22%3EBrak zdj%C4%99cia%3C/text%3E%3C/svg%3E';

type SortOption = 'popularity' | 'name';

interface PlantData {
  id: string;
  name: string;
  name_en?: string;
  description?: string;
  description_en?: string;
  image_url: string;
  popularity?: number;
  plant_sizes: SizeData[];
}

interface SizeData {
  id: string;
  height: string;
  wholesale_price: number;
  retail_price: number;
  stock_quantity?: number;
  available?: boolean;
}

export default function CatalogPage() {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [expandedSizes, setExpandedSizes] = useState<string[]>([]);
  const [plants, setPlants] = useState<PlantData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('popularity');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());
  const language = useLanguageStore((state) => state.language);
  const { isWholesale, setWholesale, addItem, items: cartItems, cartPriceMode, clearCart } = useCartStore();
  const t = translations[language];

  useEffect(() => {
    const fetchPlants = async () => {
      try {
        setLoading(true);
        setError(null);

        let query = supabase
          .from('plants')
          .select('*, plant_sizes(*)');

        if (sortBy === 'popularity') {
          query = query.order('popularity', { ascending: sortDirection === 'asc' });
        } else if (sortBy === 'name') {
          query = query.order('name', { ascending: true });
        }

        const { data: plantsData, error: plantsError } = await query;

        if (plantsError) throw plantsError;

        const processedPlants = (plantsData || []).map((plant: any) => ({
          ...plant,
          plant_sizes: (plant.plant_sizes || []).sort((a: any, b: any) => {
            const heightA = parseInt(a.height.replace(/[^\d]/g, '')) || 0;
            const heightB = parseInt(b.height.replace(/[^\d]/g, '')) || 0;
            return heightA - heightB;
          })
        }));

        setPlants(processedPlants);
      } catch (err) {
        console.error('Error fetching plants:', err);
        setError(t.catalog.fetchError);
      } finally {
        setLoading(false);
      }
    };

    fetchPlants();
  }, [sortBy, sortDirection, t]);

  const toggleDescription = (plantId: string) => {
    setExpandedItems(prev =>
      prev.includes(plantId)
        ? prev.filter(id => id !== plantId)
        : [...prev, plantId]
    );
  };

  const toggleSizes = (plantId: string) => {
    setExpandedSizes(prev =>
      prev.includes(plantId)
        ? prev.filter(id => id !== plantId)
        : [...prev, plantId]
    );
  };

  const getQuantity = (plantId: string, height: string) => {
    return quantities[`${plantId}-${height}`] || 1;
  };

  const setQuantity = (plantId: string, height: string, value: number) => {
    setQuantities(prev => ({ ...prev, [`${plantId}-${height}`]: Math.max(1, value) }));
  };

  const handlePriceModeSwitch = (newWholesale: boolean) => {
    if (newWholesale === isWholesale) return;

    if (cartItems.length > 0) {
      const currentMode = cartPriceMode;
      const requestedMode = newWholesale ? 'wholesale' : 'retail';
      if (currentMode && currentMode !== requestedMode) {
        toast.error(t.catalog.cartModeConflict, { duration: 5000 });
        return;
      }
    }
    setWholesale(newWholesale);
  };

  const handleAddToCart = (plant: PlantData, size: SizeData) => {
    const key = `${plant.id}-${size.height}`;
    const quantity = quantities[key] || 1;
    const currentMode = isWholesale ? 'wholesale' : 'retail';

    if (cartItems.length > 0 && cartPriceMode && cartPriceMode !== currentMode) {
      toast.error(t.catalog.cartModeConflict, { duration: 5000 });
      return;
    }

    const price = isWholesale ? size.wholesale_price : size.retail_price;

    addItem({
      plantId: plant.id,
      plantName: plant.name,
      plantNameEn: plant.name_en,
      height: size.height,
      price,
      quantity,
      imageUrl: plant.image_url,
    });

    const displayName = language === 'en' && plant.name_en ? plant.name_en : plant.name;
    toast.success(`${quantity}x ${displayName} (${size.height})`);
    setQuantities(prev => ({ ...prev, [key]: 1 }));
  };

  const handleImageError = (plantId: string) => {
    setImgErrors(prev => new Set(prev).add(plantId));
  };

  const getImageSrc = (plant: PlantData) => {
    if (imgErrors.has(plant.id)) return FALLBACK_IMAGE;
    return plant.image_url || FALLBACK_IMAGE;
  };

  const renderSizeRow = (plant: PlantData, size: SizeData) => {
    const key = `${plant.id}-${size.height}`;
    return (
      <div key={size.height} className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-gray-800 dark:text-gray-200 font-medium">{size.height}</span>
            {size.stock_quantity !== undefined && size.stock_quantity !== null && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t.catalog.inStock}: {size.stock_quantity} {t.catalog.pieces}
              </span>
            )}
          </div>
          <span className="font-bold text-lg text-green-700 dark:text-green-400">
            {(isWholesale ? size.wholesale_price : size.retail_price)?.toFixed(2)} zł
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
            <button
              onClick={() => setQuantity(plant.id, size.height, getQuantity(plant.id, size.height) - 1)}
              className="px-2 py-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <Minus className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
            </button>
            <input
              type="number"
              min="1"
              value={getQuantity(plant.id, size.height)}
              onChange={(e) => setQuantity(plant.id, size.height, parseInt(e.target.value) || 1)}
              className="w-14 px-1 py-1.5 text-center border-x border-gray-300 dark:border-gray-600 bg-transparent text-gray-900 dark:text-white text-sm"
            />
            <button
              onClick={() => setQuantity(plant.id, size.height, getQuantity(plant.id, size.height) + 1)}
              className="px-2 py-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <Plus className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
          <button
            onClick={() => handleAddToCart(plant, size)}
            className="flex-1 bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-1.5 font-medium text-sm"
          >
            <ShoppingCart className="w-4 h-4" />
            {t.catalog.addToCart}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">{t.catalog.title}</h1>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">{t.catalog.title}</h1>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t.catalog.title}</h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600 dark:text-gray-300">{t.catalog.sortBy}</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="popularity">{t.catalog.popularity}</option>
                <option value="name">{t.catalog.alphabetically}</option>
              </select>
            </div>
            <button
              onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              {sortDirection === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t.catalog.priceMode}:</span>
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
              <button
                onClick={() => handlePriceModeSwitch(true)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  isWholesale
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                {t.catalog.wholesale}
              </button>
              <button
                onClick={() => handlePriceModeSwitch(false)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  !isWholesale
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                {t.catalog.retail}
              </button>
            </div>
            {cartItems.length > 0 && cartPriceMode && (
              <button
                onClick={() => {
                  clearCart();
                  toast.success(t.catalog.cartCleared);
                }}
                className="text-xs text-red-600 dark:text-red-400 underline hover:no-underline ml-2"
              >
                {t.catalog.clearCartToSwitch}
              </button>
            )}
          </div>
          {isWholesale && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2">
              <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">{t.catalog.wholesaleInfo}</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plants.map((plant) => (
          <div key={plant.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow">
            <div className="aspect-square relative overflow-hidden bg-gray-100 dark:bg-gray-700">
              <img
                src={getImageSrc(plant)}
                alt={language === 'en' ? plant.name_en || plant.name : plant.name}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={() => handleImageError(plant.id)}
              />
            </div>
            <div className="p-4">
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                {language === 'en' ? plant.name_en || plant.name : plant.name}
              </h2>

              {(language === 'en' ? plant.description_en : plant.description) && (
                <div className="mb-4">
                  <button
                    onClick={() => toggleDescription(plant.id)}
                    className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 flex items-center text-sm"
                  >
                    {expandedItems.includes(plant.id) ? (
                      <>{t.catalog.hideDescription}<ChevronUp className="w-4 h-4 ml-1" /></>
                    ) : (
                      <>{t.catalog.showDescription}<ChevronDown className="w-4 h-4 ml-1" /></>
                    )}
                  </button>
                  {expandedItems.includes(plant.id) && (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {language === 'en' ? plant.description_en : plant.description}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm text-gray-900 dark:text-white">{t.catalog.availableSizes}</p>
                  {plant.plant_sizes.length > 1 && (
                    <button
                      onClick={() => toggleSizes(plant.id)}
                      className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 flex items-center text-xs"
                    >
                      {expandedSizes.includes(plant.id) ? (
                        <>{t.catalog.collapse}<ChevronUp className="w-3 h-3 ml-1" /></>
                      ) : (
                        <>{t.catalog.expandAll} ({plant.plant_sizes.length})<ChevronDown className="w-3 h-3 ml-1" /></>
                      )}
                    </button>
                  )}
                </div>

                {plant.plant_sizes.length > 0 && renderSizeRow(plant, plant.plant_sizes[0])}

                {expandedSizes.includes(plant.id) && (
                  <div className="space-y-2">
                    {plant.plant_sizes.slice(1).map((size) => renderSizeRow(plant, size))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
